import React, { useContext, useEffect, useMemo, useState } from "react";
import { Timestamp } from "firebase/firestore";
import { Odd } from "../../../../models/Odd_response_model.tsx";
import { BetDetails, ParlaySelection } from "../../../../models/MyBetModel.tsx";
import { useBetContext } from "../../../../context/ParlayBetContext.tsx";
import { ParlaySelectorComponent } from "./ParlaySelectorComponent.tsx";
import Alert from "../../../alerts/Alert.tsx";
import { Users } from "../../../../models/User.tsx";
import SportContext, { useSport } from "../../../../context/SportContext.tsx";
import { ALLOWED_MARKETS, buildMlbPitchers, extractPeriod, filterPreferredSportsbookOdds, formatMarketTitle, getName, isSameSelectionSide, PeriodTeamSuffixLabel } from "../../../../helpers/functions.tsx";
import AlertError from "../../../alerts/AlertError.tsx";
import ImageWithPlaceholder from "../../../ImageWithPlaceholder.tsx";
import { DEFAULT_PROPS_LOGO, logoForSelectionLine } from "../../modals/BetLogo.tsx";
import { useModal } from "../../../../context/ModalContext.tsx";
import ParlayBets from "../../modals/parlay/ParlayBets.tsx";
import { fetchOddsSnapshot } from "../../../../helpers/oddsSnapshotCache.tsx";
import { applyRefreshedParlayOdds } from "../../../../helpers/parlayOddsRefresh.ts";
import { SlotConfig } from "../../../../models/Sport.tsx";

interface GameTeamComponentProps {
    gameId: string,
    sport: string,
    team: string,
    odds: Odd[],
    league: string,
    gameTime: Date;
    setShowLimitAlert: React.Dispatch<React.SetStateAction<boolean>>,
    fixture_id: string,
    gameLogo: string,
    opposingTeam: string,
    pitcherHome: string,
    pitcherAway: string,
    pitcherHomeId?: string | null,
    pitcherAwayId?: string | null,
    pitchersChecked: any,
    showFlag?: boolean;
    setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
    user: Users;
    logoLeague: string
}

export function ParlayGameTeam(props: GameTeamComponentProps) {

    const { gameId, sport, team, odds, league, fixture_id, gameLogo, opposingTeam, showFlag,
        setErrorMessage, user, setShowLimitAlert } = props;
    const { bets, updateQuee, deleteBet, setBets } = useBetContext();
    const { marketOrderFromDB } = useSport();
    const [teamBets, setTeamBets] = useState<BetDetails[]>(bets.filter(existingBet => existingBet.gameId === gameId && existingBet.teamId === team));
    const gameTime = Timestamp.fromDate(props.gameTime);
    const totalBets = bets.length;
    const [showAlert, setShowAlert] = useState(false);
    const [loadingModal, setLoadingModal] = useState(false);
    const marketTitle = formatMarketTitle(odds?.[0]?.market, league);
    const rawName = odds?.[0]?.market || "";
    const period = extractPeriod(rawName);
    const shouldShowMarketTitle = !!period && ALLOWED_MARKETS.has(period);
    const { showModal } = useModal();

    const onShowBetsModal = async () => {
        if (loadingModal) return;
        setLoadingModal(true);

        try {
            const roundOddsPrice = (price: number) => {
                return Math.trunc(price / 5) * 5;
            };

            const refreshedBets = await Promise.all(
                bets.map(async (bet) => {
                    if (!bet.fixture_id || !bet.odd?.market_id) return bet;
            
                    try {
                        const scheduleData = await fetchOddsSnapshot({
                            id: bet.fixture_id,
                            marketId: bet.odd.market_id,
                            team_id: bet.odd.team_id,
                            sportsbooks: [bet.odd.sportsbook],
                        });
            
                        const freshOdds = (scheduleData[0]?.odds ?? [])
                            .filter((o: Odd) => isSameSelectionSide(bet.odd!, o))
                            .map((odd: Odd) => ({
                                ...odd,
                                price: roundOddsPrice(odd.price)
                            }));
            
                        if (freshOdds.length === 0) return bet;

                        return applyRefreshedParlayOdds(bet, freshOdds);
            
                    } catch (err) {
                        console.error('Error refreshing odds for parlay bet:', err);
                        return bet;
                    }
                })
            );

            setBets(refreshedBets);
        } finally {
            setLoadingModal(false);
            showModal(<ParlayBets
                setShowLimitAlert={setShowLimitAlert}
                setErrorMessage={setErrorMessage} />);
        }
    };

    useEffect(() => {
        const betsTeam = bets.filter(existingBet =>
            existingBet.gameId === gameId &&
            existingBet.teamId === team);

        if (betsTeam.length !== teamBets.length) {
            setTeamBets(betsTeam);
        }
    }, [bets]);

    const _build = (selected: Odd, odds: Odd[]): ParlaySelection => {

        const pitchers = buildMlbPitchers({
            league,
            marketName: selected.market,
            pitcherAway: props.pitcherAway,
            pitcherHome: props.pitcherHome,
            pitcherAwayId: props.pitcherAwayId,
            pitcherHomeId: props.pitcherHomeId,
            checkedAway: props.pitchersChecked?.away,
            checkedHome: props.pitchersChecked?.home,
        });

        let selectionType: string | undefined;
        if (selected.market === "Moneyline") {
            selectionType = "ML";
        } else if (selected.market === "Total Points" || selected.market === "Team Total") {
            if (selected.selection_line) {
                if (selected.selection_line.toLowerCase() === "over") {
                    selectionType = "O";
                } else if (selected.selection_line.toLowerCase() === "under") {
                    selectionType = "U";
                } else {
                    selectionType = selected.selection_line;
                }
            }
        }

        const logoToShow = logoForSelectionLine(selected.selection_line, gameLogo || "");

        let marketDisplay = ''
        if (shouldShowMarketTitle) {
            marketDisplay = formatMarketTitle(selected.market, league);
        }


        const newBet = {
            id_selection: selected.id,
            gameId: gameId,
            league: league,
            sport: sport,
            marketName: selected.market,
            gameDate: gameTime,
            teamId: team,
            betName: selected.name,
            fixture_id: fixture_id,
            betStatus: 'pending',
            odd: selected,
            odds,
            opposingTeam: opposingTeam,
            logo: logoToShow,
            ...(pitchers ? { pitchers } : {}),
            marketType: marketDisplay,
            ...(selectionType ? { selectionType } : {})
        } as ParlaySelection;

        return newBet;
    };

    const onRequestBetsModal = (update?: Partial<ParlaySelection>, selected: boolean = true) => {
        if (user.status !== "Active") {
            setErrorMessage("Your account is in read-only mode."
            );
            return;
        }
        const odd = update?.odd;
        if (odd === undefined) {
            return;
        }
        const filteredOdds = odds.filter(o =>
            o.market_id === odd.market_id &&
            isSameSelectionSide(odd, o)
        );

        if (selected) {
            //TODO: add filter for selection_line
            updateQuee(_build(odd, filteredOdds));
        } else {
            deleteBet(update);
        }
    };

    const oddsMatrix = useMemo(() => {
        if (odds.length === 0 || marketOrderFromDB.length === 0) {
            return [];
        }

        const oddsByMarket = Object.groupBy(odds, odd => odd.market_id);
        return [...marketOrderFromDB]
            .sort((a: SlotConfig, b: SlotConfig) => a.slot - b.slot)
            .map((slot: SlotConfig) =>
                slot.marketIds.flatMap((marketId: string) => {
                    const localOdds = oddsByMarket[marketId] ?? [];
                    return localOdds.length > 0
                        ? filterPreferredSportsbookOdds(localOdds)
                        : localOdds;
                })
            );
    }, [odds, marketOrderFromDB]);

    const slotsToRender = () => {
        const isSmallScreen = window.innerWidth < 776;
        const isUFC = league?.toUpperCase() === "UFC";

        let slots;

        if (isSmallScreen) {
            const firstSlot = oddsMatrix[0]?.length === 0 ? oddsMatrix[1] : oddsMatrix[0];
            const secondSlot = oddsMatrix[0]?.length === 0 ? oddsMatrix[0] : oddsMatrix[1];

            slots = [
                firstSlot ?? [],
                secondSlot ?? [],
                oddsMatrix[2] ?? [],
                oddsMatrix[3] ?? [],
            ];
        } else {
            slots = [
                oddsMatrix[0] ?? [],
                oddsMatrix[1] ?? [],
                oddsMatrix[2] ?? [],
                oddsMatrix[3] ?? [],
            ];
        }

        if (isUFC) {
            if (isSmallScreen) {
                slots[0] = oddsMatrix[1] ?? [];
                slots[1] = [];
            }
        }

        return slots;
    };

    const renderedSlots = slotsToRender();
    const isSmallScreen = window.innerWidth < 776;

    return (
        <div className="line-container">
            {showAlert && (
                <Alert
                    message="At least 2 select are required for a parlay."
                    onClose={() => setShowAlert(false)}
                />
            )}

            {bets.length !== 0 && (
                <div className='containerButtonParlay'>
                    <button className='btn-custom'
                        disabled={loadingModal}
                        onClick={() => {
                            if (bets.length === 1) {
                                setShowAlert(true);
                            } else {
                                onShowBetsModal();
                            }
                        }}>
                        {loadingModal ? 'Loading...' : `${totalBets} Pick(s)`}
                    </button>
                </div>
            )}
            <div className={`team-name-container props ${showFlag ? "single-flag" : ""}`} style={{ display: "flex", alignItems: "center" }}>
                <ImageWithPlaceholder
                    src={gameLogo || ""}
                    className="sport-logos"
                    alt=""
                    placeholderClassName="placeholder-logos-team"
                    fallbackSrc={DEFAULT_PROPS_LOGO}
                />
                <span className="teko team-name name-small" title={team}>{team}<PeriodTeamSuffixLabel market={rawName} league={league} /></span>
                <span className="teko team-name name-big" title={team}>
                    {/* {getShortName(team)} */}
                        {
                            getName(team, league)
                        }<PeriodTeamSuffixLabel market={rawName} league={league} />
                </span>&nbsp;
            </div>


            {renderedSlots.map((slotOdds, i) => {
                const marketId = slotOdds.at(0)?.market_id ?? `empty-${i}`;
                const isUFC = league?.toUpperCase() === "UFC";
                if (isUFC && i === 0 && !isSmallScreen) return null;
                return (
                    <div
                        key={i}
                        className={`market-${i} ${i === 0 || i === 2 ? "" : "d-none"} d-lg-flex market-center main-markets market-${marketId}`}
                    >
                        {slotOdds.length > 0 ? (
                            <ParlaySelectorComponent
                                availableOdds={slotOdds}
                                onRequestBetsModal={onRequestBetsModal}
                                slotIndex={i}
                            />
                        ) : (
                            <div className={`empty-market market-${marketId}`}></div>
                        )}
                    </div>
                );
            })}
        </div>
    )
}
